// App — route tree for bubble-wu-auditor.
//
// Route hierarchy:
//   /login            → SignInPage (public)
//   RequireAuth (layout) — all routes below require a session
//     /connect        → ConnectBubblePage (auth required — API key POST needs session)
//     IntegrationGate (layout — redirects to /connect if no active Bubble integration)
//       AppShell (layout)
//         /           → DashboardPage
//         /apps       → AppsPage
//         /apps/:appId/audits → AuditListPage

import { useEffect } from "react";
import { Routes, Route, NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, AppWindow } from "lucide-react";
import { AppShell } from "@avagency/frontend/components/shell/AppShell";
import { RequireAuth } from "@avagency/frontend/components/auth/RequireAuth";
import { SignInPage } from "./pages/SignInPage";
import { ConnectBubblePage } from "./pages/ConnectBubblePage";
import { DashboardPage } from "./pages/DashboardPage";
import { AppsPage } from "./pages/AppsPage";
import { AuditListPage } from "./pages/AuditListPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { TermsPage } from "./pages/TermsPage";
import { IntegrationGate } from "./components/IntegrationGate";

export default function App() {
  useEffect(() => {
    document.title = "Bubble WU Auditor";
  }, []);

  return (
    <Routes>
      {/* ── Public ── */}
      <Route path="/login" element={<SignInPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* ── Protected — session required ── */}
      <Route element={<RequireAuth redirectTo="/login" />}>
        {/* /connect: auth required (API key POST goes to session-guarded endpoint) */}
        <Route path="/connect" element={<ConnectBubblePage />} />

        {/* Redirects to /connect if no active Bubble integration */}
        <Route element={<IntegrationGate />}>
          <Route element={<AppShellLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="/apps" element={<AppsPage />} />
            <Route path="/apps/:appId/audits" element={<AuditListPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}

// AppShell accepts `children` not <Outlet/> automatically, so wrap it.
function AppShellLayout() {
  return (
    <AppShell
      productSlug="bubble"
      topbar={<TopbarBrand />}
      sidebar={<Sidebar />}
    >
      <Outlet />
    </AppShell>
  );
}

function TopbarBrand() {
  return (
    <div className="flex items-center gap-2.5">
      {/* Mini orb */}
      <div
        className="flex-shrink-0 rounded-full"
        style={{
          width: 22,
          height: 22,
          background:
            "radial-gradient(circle at 35% 30%, #f8faff 0%, #a9b8d4 22%, #3b5fa0 48%, #0a1740 80%)",
          boxShadow: "0 0 0 1px rgba(255,255,255,0.12), 0 2px 8px rgba(59,130,246,0.25)",
        }}
        aria-hidden="true"
      />
      <span
        className="text-sm font-semibold"
        style={{ letterSpacing: "-0.01em", color: "hsl(220 18% 88%)" }}
      >
        Bubble WU Auditor
      </span>
      <span
        className="hidden sm:inline text-xs"
        style={{ color: "hsl(218 14% 46%)" }}
      >
        Workflow Unit monitoring
      </span>
    </div>
  );
}

// AppShell already wraps sidebar content in a <nav> landmark — return <ul> only.
function Sidebar() {
  return (
    <ul className="space-y-0.5">
      <SidebarLink to="/" end icon={<LayoutDashboard className="h-4 w-4" />} label="Dashboard" />
      <SidebarLink to="/apps" icon={<AppWindow className="h-4 w-4" />} label="Apps" />
    </ul>
  );
}

interface SidebarLinkProps {
  to: string;
  end?: boolean;
  icon: React.ReactNode;
  label: string;
}

function SidebarLink({ to, end, icon, label }: SidebarLinkProps) {
  return (
    <li>
      <NavLink
        to={to}
        end={end}
        className={({ isActive }) =>
          [
            "flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-surface",
            isActive
              ? "sidebar-link-active"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
          ].join(" ")
        }
        aria-current={undefined}
      >
        <span aria-hidden="true">{icon}</span>
        {label}
      </NavLink>
    </li>
  );
}
